import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { Dropdown } from '../../../../../../../web/components/Dropdown';
import { Button } from '../../../../../../../web/components-v2/Button';
import { useApiClientContext } from '../../../../../../context';
import { IEventRegistrationSurvey, IOperationResultNoValue } from '../../../../../../models/Api';
import { styleSheet } from './styles';

type CalendarLinkProperties = {
	responseId: string;
	details: string;
	name: string;
	location: string;
	timeZone: string;
};

type DateTimeInformation = {
	startDateTimeMoment: moment.Moment;
	endDateTimeMoment: moment.Moment;
};

enum CalendarLinkType {
	GoogleCalendar = 'Google Calendar',
	Outlook = 'Outlook.com',
	ICS = 'iCal File',
	Apple = 'Apple',
	Outlook365 = 'Outlook365',
	Yahoo = 'Yahoo',
}

const calendarAction = async (
	calendarLinkProperties: CalendarLinkProperties,
	dateTimeInformation: DateTimeInformation,
	calendarLinkType: CalendarLinkType,
	getEventIcal: () => Promise<string>
) => {
	for (const [key, value] of Object.entries(calendarLinkProperties)) {
		calendarLinkProperties[key as keyof CalendarLinkProperties] = encodeURIComponent(value);
	}
	const { details, name, location, timeZone } = calendarLinkProperties;
	const { startDateTimeMoment, endDateTimeMoment } = dateTimeInformation;
	// Note: `window.open` can return `null` if the browser fails to open the new browsing context,
	// for example because it was blocked by a browser popup blocker"
	switch (calendarLinkType) {
		case CalendarLinkType.Yahoo: {
			const startDateTime = startDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			const endDateTime = endDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			return window
				.open(
					`https://calendar.yahoo.com/?v=60&st=${startDateTime}&et=${endDateTime}&title=${name}&in_loc=${location}&desc=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.Outlook365: {
			const startDateTime = startDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			const endDateTime = endDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			return window
				.open(
					`https://outlook.office.com/calendar/action/compose?rru=addevent&startdt=${startDateTime}&enddt=${endDateTime}&subject=${name}&location=${location}&body=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.Apple:
		case CalendarLinkType.ICS: {
			// The server generates the .ics with a DST-aware VTIMEZONE and a UID that matches the
			// emailed invite, so re-imports update one entry instead of duplicating.
			const ics = await getEventIcal();
			return window.open('data:text/calendar;charset=utf8,' + encodeURIComponent(ics));
		}
		case CalendarLinkType.Outlook: {
			const startDateTime = startDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			const endDateTime = endDateTimeMoment.format('YYYY-MM-DD[T]HH:mm:ss');
			return window
				.open(
					`https://outlook.live.com/calendar/action/compose?rru=addevent&startdt=${startDateTime}&enddt=${endDateTime}&subject=${name}&location=${location}&body=${details}`,
					'_blank'
				)
				?.focus();
		}
		case CalendarLinkType.GoogleCalendar:
		default: {
			const startDateTime = startDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			const endDateTime = endDateTimeMoment.format('YYYYMMDD[T]HHmmss');
			return window
				.open(
					`https://calendar.google.com/calendar/u/0/r/eventedit?dates=${startDateTime}/${endDateTime}&ctz=${timeZone}&text=${name}&location=${location}&details=${details}`,
					'_blank'
				)
				?.focus();
		}
	}
};

type Props = {
	surveyResponseId: string;
	survey: IEventRegistrationSurvey;
	token: string;
};

export const AddToCalendarDropdown = ({ survey, surveyResponseId, token }: Props) => {
	const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
	const { apiClient } = useApiClientContext();
	const getEventIcal = async () => {
		const ics = await apiClient.get<string>(
			`survey/eventregistration/response/byToken/ical?token=${encodeURIComponent(token)}`
		);
		if (!ics) {
			throw new Error('Received an empty calendar file');
		}
		return ics;
	};
	const timeZone = survey.eventInformation.timeZone;
	const startDateTimeMoment = moment.tz(survey.eventInformation.startTime, timeZone);
	const endDateTimeMoment = survey.eventInformation.endTime
		? moment.tz(survey.eventInformation.endTime, timeZone)
		: moment(startDateTimeMoment).add(1, 'hour');
	return (
		<Dropdown
			openOnClick
			isOpen={isDropdownOpen}
			onOpenChanged={setIsDropdownOpen}
			contentPositionY='top'
			contentClassName={css(styleSheet.dropdownMenu)}
			anchor={
				<Button variant='secondary' styles={[styleSheet.dropdownButton]}>
					Add to Calendar
				</Button>
			}
		>
			<>
				{Object.values(CalendarLinkType).map(calendarType => (
					<div
						className={css(styleSheet.menuItem)}
						key={calendarType}
						onClick={async () => {
							setIsDropdownOpen(false);
							try {
								await calendarAction(
									{
										name: survey.name,
										responseId: surveyResponseId,
										// Calendar links and ICS files require plain text; detailsContent.document is rich HTML markup.
										details: survey.eventInformation.detailsPlainText || survey.eventInformation.details || '',
										location: survey.eventInformation.location,
										timeZone: survey.eventInformation.timeZone,
									},
									{ startDateTimeMoment, endDateTimeMoment },
									calendarType,
									getEventIcal
								);
							} catch (error) {
								toast.error(
									(error as IOperationResultNoValue)?.systemMessage || 'Unable to add this event to your calendar.'
								);
							}
						}}
					>
						{calendarType}
					</div>
				))}
			</>
		</Dropdown>
	);
};
